import humps from 'humps';
import type { Context } from '@nbx/frontend-helpers/auth';
import { get } from '@nbx/frontend-helpers/fetch-api';
import type { MarketsFromJWT } from '@nbx/frontend-helpers/types';

export type MarketMode = 'RFQ' | 'OB';

export interface Market {
  baseAsset: string;
  baseCurrency: string;
  baseIncrement: string;
  baseSizeMinimum: string;
  cancelOnly: boolean;
  disabled: boolean;
  id: string;
  limitOnly: boolean;
  makerFee?: string;
  minQty: number;
  minQtyLength: number;
  minTick: number;
  minTickLength: number;
  mode?: MarketMode;
  name: string;
  pair: string;
  quoteAsset: string;
  quoteCurrency: string;
  quoteIncrement: string;
  status: string;
  statusDescription?: string;
  takerFee?: string;
  isQuickbuyEnabled: boolean;
  quickbuyBaseAssetMaximum: string;
  quickbuyQuoteAssetMaximum: string;
}

const incrementDecimals = (increment: string) => `${increment}`.split('.')[1]?.length ?? 0;

const DEFAULT_MAKER_FEE = '0.5';
const DEFAULT_TAKER_FEE = '0.7';

export const getMarketsBaseUrl = (): string | undefined => window.API_CONFIG?.api.markets?.baseUrl;

export const fetchMarkets = (accountContext?: Context): Promise<Array<Market>> =>
  get(`${getMarketsBaseUrl()}/`, accountContext) as Promise<Array<Market>>;

export function formatMarkets(
  marketsInfo: Array<Market>,
  marketsFromJWT: MarketsFromJWT = {}
): Array<Market> {
  const configMakerFee = window.API_CONFIG?.markets?.previewTradingFee ?? DEFAULT_MAKER_FEE;
  const configTakerFee = window.API_CONFIG?.markets?.previewTradingTakerFee ?? DEFAULT_TAKER_FEE;
  try {
    const formattedMarkets = marketsInfo.map(market => {
      const formattedJWTMarket: Partial<Market> = humps.camelizeKeys(marketsFromJWT[market.id]) ?? {};
      try {
        return {
          ...market,
          pair: market.id,
          // Translate to old data format for compat
          baseCurrency: market.baseAsset,
          quoteCurrency: market.quoteAsset,
          minTick: Number.parseFloat(market.quoteIncrement),
          minQty: Number.parseFloat(market.baseIncrement),
          minTickLength: incrementDecimals(market.quoteIncrement),
          minQtyLength: incrementDecimals(market.baseIncrement),
          // Override trading fees with value from JWT, fallback to config, then to hardcoded default
          makerFee: market.disabled
            ? formattedJWTMarket.makerFee
            : (formattedJWTMarket.makerFee ?? configMakerFee),
          takerFee: market.disabled
            ? formattedJWTMarket.takerFee
            : (formattedJWTMarket.takerFee ?? configTakerFee)
        };
      } catch (e) {
        console.error('[fetchAndFormatMarkets] error in formattedMarkets for market = ', market);
        throw e;
      }
    });
    return formattedMarkets;
  } catch (e) {
    console.error(
      '[fetchAndFormatMarkets], error in fetchAndFormatMarkets, marketsInfo = ',
      marketsInfo,
      ', marketsFromJWT = ',
      marketsFromJWT
    );
    throw e;
  }
}
